import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ExternalLink, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { AppHeader } from "@/components/Layout";
import { StatusPill } from "@/components/RecallCard";
import { api, errMsg, fmtDate, TYPE_LABEL } from "@/lib/api";
import { useSeo } from "@/lib/seo";

const Field = ({ label, value, testid }) => (
  <div className="border-t border-slate-100 py-4">
    <p className="label-caps">{label}</p>
    <p className="mt-1.5 text-slate-800" data-testid={testid}>{value || "Refer to the official FSANZ notice"}</p>
  </div>
);

export default function RecallDetail() {
  const { id } = useParams();
  const [r, setR] = useState(null);

  useEffect(() => {
    api.get(`/recalls/${id}`).then(setR).catch((e) => toast.error(errMsg(e)));
  }, [id]);

  useSeo({
    title: r
      ? `${r.product_name} recall - ${r.company || "Australian food recall"}`
      : "Australian food recall",
    description: r
      ? `${r.product_name}${r.company ? ` from ${r.company}` : ""} was recalled in Australia: ${r.short_reason || r.reason || "see the official notice"}. Distribution: ${r.location_label || "Australia"}. Official FSANZ recall details and consumer action.`
      : "Official Australian food recall details, sourced from Food Standards Australia New Zealand.",
    path: `/recall/${id}`,
  });

  if (!r) return (<div className="min-h-screen bg-white"><AppHeader /><p className="p-10 text-slate-500">Loading...</p></div>);

  return (
    <div className="min-h-screen bg-white">
      <AppHeader />
      <div className="mx-auto max-w-3xl px-5 py-8 sm:py-12">
        <Link to="/alerts" data-testid="back-to-feed" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900">
          <ChevronLeft className="h-4 w-4" /> All recalls
        </Link>
        <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_3px_rgba(15,23,42,0.04)]">
          {r.image_url && (
            <div className="h-48 w-full bg-slate-100 sm:h-60">
              <img src={r.image_url} alt={`${r.product_name} - Australian food recall`} className="h-full w-full object-cover" />
            </div>
          )}
          <div className="p-5 sm:p-8">
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill recall={r} />
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                {TYPE_LABEL[r.recall_type] || "Recall"}
              </span>
            </div>
            <h1 className="mt-5 break-words font-display text-2xl font-extrabold tracking-tighter text-slate-900 sm:text-4xl" data-testid="detail-product">
              {r.product_name} {r.product_size && `- ${r.product_size}`}
            </h1>
            <p className="mt-2 text-slate-600" data-testid="detail-company">{r.company || "See official notice"}</p>

            <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-4 sm:mt-7 sm:p-5">
              <p className="label-caps text-red-700">Recall reason</p>
              <p className="mt-1 font-semibold text-red-800" data-testid="detail-reason">{r.short_reason}</p>
              <p className="mt-1 text-sm text-red-700">{r.reason}</p>
            </div>

            <Field label="Affected states" value={r.location_label} testid="detail-states" />
            <Field label="Distribution (as published)" value={r.distribution_original} testid="detail-distribution" />
            <Field label="Published" value={fmtDate(r.published_at)} testid="detail-published" />
            <Field label="Affected batch / date markings" value={r.date_marking} testid="detail-date-marking" />
            <Field label="Why it was recalled" value={r.hazard} testid="detail-hazard" />
            <Field label="What consumers should do" value={r.consumer_action} testid="detail-consumer-action" />
            <Field label="Company contact" value={r.contact_details} testid="detail-contact" />

            <a
              href={r.source_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="official-fsanz-link"
              className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-slate-900 px-7 py-4 text-base font-semibold text-white transition-colors hover:bg-slate-800"
            >
              View official FSANZ recall <ExternalLink className="h-4 w-4" />
            </a>
            <p className="mt-4 text-xs leading-relaxed text-slate-500" data-testid="detail-disclaimer">
              Recall information sourced from Food Standards Australia New Zealand. Always refer to the official FSANZ
              notice for complete and current information.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
